import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNearbyBillboards } from '@/features/billboards/api/useBillboardQueries';
import {
  NEARBY_DEFAULT_CENTER,
  NEARBY_DEFAULT_RADIUS,
  extractNearbyBillboards,
} from '@/features/billboards/utils/nearbyBillboardsUtils';
import { useAppSelector } from '@/shared/store/hooks';

type Coordinates = { lat: number; lng: number };

const CITY_COORDINATES: Record<string, Coordinates> = {
  bhopal: { lat: 23.2599, lng: 77.4126 },
  delhi: { lat: 28.7041, lng: 77.1025 },
  indore: { lat: 22.7196, lng: 75.8577 },
  mumbai: { lat: 19.076, lng: 72.8777 },
};

export function useNearbyBillboardsPage() {
  const storeCity = useAppSelector((s) => s.filters.city);
  const city = storeCity?.trim() || undefined;
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [selectedRadius, setSelectedRadius] = useState<number>(
    NEARBY_DEFAULT_RADIUS,
  );
  const [locationError, setLocationError] = useState(false);

  const fallbackCenter =
    (city && CITY_COORDINATES[city.toLowerCase()]) || NEARBY_DEFAULT_CENTER;

  const applyLocation = useCallback((lat: number, lng: number) => {
    setUserLocation({ lat, lng });
    setLocationError(false);
  }, []);

  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      applyLocation(fallbackCenter.lat, fallbackCenter.lng);
      setLocationError(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        applyLocation(position.coords.latitude, position.coords.longitude);
      },
      () => {
        applyLocation(fallbackCenter.lat, fallbackCenter.lng);
        setLocationError(true);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [applyLocation, fallbackCenter]);

  useEffect(() => {
    detectLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const query = useNearbyBillboards(
    userLocation
      ? {
          lat: userLocation.lat,
          lng: userLocation.lng,
          radius: selectedRadius,
          city,
        }
      : undefined,
  );

  const billboards = useMemo(
    () => extractNearbyBillboards(query.data, city),
    [query.data, city],
  );

  return {
    userLocation,
    selectedRadius,
    setSelectedRadius,
    locationError,
    billboards,
    isPending: query.isPending,
    isError: query.isError,
    handleRecenter: detectLocation,
  };
}
